import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Check, Loader2 } from "lucide-react";
import BotanicalSprig from "@/components/BotanicalSprig";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { useCardName } from "@/hooks/useCardName";
import { useAuth } from "@/hooks/useAuth";
import { useAchievementStats } from "@/hooks/useAchievementStats";
import { MILESTONES, MILESTONE_COPY, type Milestone, type AchievementStats, shareAchievement, ensureBrandFonts, renderAchievementCard } from "@/lib/achievement-card";

const seenKey = (userId: string, m: number) => `sana_milestone_seen_${userId}_${m}`;

export function useShareAchievement() {
  const [sharing, setSharing] = useState(false);
  const share = async (milestone: number, stats: AchievementStats, opts?: Parameters<typeof shareAchievement>[2]) => {
    setSharing(true);
    try {
      const result = await shareAchievement(milestone, stats, opts);
      if (result === "downloaded") toast.success("Imagen guardada. Subila a tus historias 💛");
      return result;
    } catch (e) {
      console.error("share achievement error:", e);
      toast.error("No pudimos crear la imagen. Intentá de nuevo.");
      return "cancelled" as const;
    } finally {
      setSharing(false);
    }
  };
  return { share, sharing };
}

export const CelebrationDialog = ({
  milestone,
  open,
  onClose,
  onShare,
  sharing,
  stats,
}: {
  milestone: Milestone | null;
  open: boolean;
  onClose: () => void;
  onShare: (name: string | null) => void;
  sharing: boolean;
  stats?: AchievementStats;
}) => {
  const { firstName, showName, setShowName, cardName } = useCardName();
  const [preview, setPreview] = useState<string | null>(null);
  useEffect(() => {
    if (!open || !milestone || !stats) return;
    let url: string | null = null;
    let alive = true;
    renderAchievementCard(milestone, stats, { name: cardName })
      .then((b) => { if (!alive) return; url = URL.createObjectURL(b); setPreview(url); })
      .catch((e) => console.error("preview error:", e));
    return () => { alive = false; if (url) URL.revokeObjectURL(url); setPreview(null); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, milestone, cardName, stats?.streak, stats?.totalMinutes, stats?.completedDays.length]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="isolate max-w-sm overflow-hidden rounded-[30px] border-[color:var(--glass-border)] bg-gradient-to-b from-[#FBF8F2] to-[#F6E3E2] p-6 text-center dark:from-[#25211D] dark:to-[#3A2A2B]">
        <BotanicalSprig />
        <div className="mx-auto mb-3 mt-2 flex h-[100px] w-[100px] items-center justify-center rounded-full bg-primary shadow-[0_14px_30px_hsl(var(--primary)/0.35)]">
          <Check className="h-12 w-12 text-primary-foreground" strokeWidth={3} />
        </div>
        <DialogTitle className="text-[28px] font-bold leading-tight text-foreground" style={{ fontFamily: '"Fraunces", Georgia, serif' }}>
          ¡Completaste tu <em className="italic text-primary">día {milestone}</em>!
        </DialogTitle>
        <DialogDescription className="text-base text-muted-foreground">
          {milestone ? MILESTONE_COPY[milestone] : ""}
        </DialogDescription>
        {stats && (
          <div className="mx-auto my-3 h-[180px] w-[101px] -rotate-3 overflow-hidden rounded-2xl bg-card shadow-[0_14px_34px_rgba(58,50,45,0.18)]">
            {preview ? <img src={preview} alt="Vista previa de tu tarjeta" className="h-full w-full object-cover" /> : <div className="h-full w-full animate-pulse bg-foreground/[0.06]" />}
          </div>
        )}
        {firstName && (
          <label className="mx-auto mb-1 flex w-fit cursor-pointer items-center gap-3 text-[15px] font-semibold text-foreground">
            <Switch checked={showName} onCheckedChange={setShowName} aria-label="Mostrar mi nombre" />
            Mostrar mi nombre
          </label>
        )}
        <div className="mt-2 space-y-2">
          <Button onClick={() => onShare(cardName)} disabled={sharing} className="w-full">
            {sharing ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : null}
            Compartir en mis historias
          </Button>
          <Button variant="secondary" onClick={onClose} className="h-11 w-full">
            Ahora no
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/** Muestra el festejo una sola vez por hito y por usuaria (guardado en localStorage). */
const MilestoneCelebration = () => {
  const { user } = useAuth();
  const stats = useAchievementStats(user?.id);
  const [milestone, setMilestone] = useState<Milestone | null>(null);
  const { share, sharing } = useShareAchievement();

  useEffect(() => {
    ensureBrandFonts();
  }, []);

  useEffect(() => {
    if (!user?.id || milestone) return;
    const reached = MILESTONES.filter((m) => stats.completedDays.includes(m));
    const unseen = reached.filter((m) => !localStorage.getItem(seenKey(user.id, m)));
    if (unseen.length === 0) return;
    // Mostrar solo el hito más alto pendiente y dar por vistos los anteriores
    reached.forEach((m) => localStorage.setItem(seenKey(user.id, m), "1"));
    setMilestone(unseen[unseen.length - 1]);
  }, [user?.id, stats.completedDays, milestone]);

  const close = () => setMilestone(null);

  return (
    <CelebrationDialog
      milestone={milestone}
      open={milestone !== null}
      stats={stats}
      onClose={close}
      sharing={sharing}
      onShare={async (name) => {
        if (!milestone) return;
        const r = await share(milestone, stats, { name });
        if (r !== "cancelled") close();
      }}
    />
  );
};

export default MilestoneCelebration;
